"use client";

import React from "react";
import Image from "next/image";
import { Section, Typography } from "@/components/ui";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";

import "swiper/css";

export interface ShowcaseImage {
  src: string;
  alt: string;
}

interface AppShowcaseSectionProps {
  title?: string;
  description?: React.ReactNode;
  showcaseImage: string;
  showcaseImageAlt: string;
  showcaseSliderImages: ShowcaseImage[];
  background?: "white" | "gray" | "red" | "gradient-red" | "gradient-light" | "transparent";
  className?: string;
}

export function AppShowcaseSection({
  title = "Screens that speak your culture.",
  description = "From lobbies to meeting rooms, Pickcel keeps your workplace informed and energised — through visual, timely communication.",
  showcaseImage,
  showcaseImageAlt,
  showcaseSliderImages,
  background = "white",
  className = "",
}: AppShowcaseSectionProps) {
  return (
    <Section variant="default" background={background} className={className}>
      <div className="max-w-7xl container mx-auto">
        {/* Title and Description */}
        <div className="text-center mb-12">
          <Typography variant="h2" as="h2" className="mb-6">
            {title}
          </Typography>
          <Typography variant="body" as="p" className="text-text-primary max-w-4xl mx-auto">
            {description}
          </Typography>
        </div>

        {/* Office Scene with Digital Display and Video Overlay */}
        <div className="relative w-full overflow-hidden rounded-sm md:rounded-lg">
          <Image
            src={showcaseImage}
            alt={showcaseImageAlt}
            title={showcaseImageAlt}
            width={1200}
            height={700}
            className="w-full h-auto rounded-lg hidden lg:block"
          />

          {/* Image Slider Overlay on Screen */}
          <div className=" md:absolute bottom-[-8px] left-1/2 md:-translate-x-1/2 aspect-video max-w-[752px] overflow-hidden shadow-2xl border-3 border-gray-800/30 bg-black">
            <Swiper
              modules={[Autoplay]}
              spaceBetween={0}
              slidesPerView={1}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              loop={true}
              className="w-full h-full [&_.swiper-pagination]:hidden"
            >
              {showcaseSliderImages.map((image, index) => (
                <SwiperSlide key={index}>
                  <div className="w-full h-full flex items-center justify-center">
                    <Image
                      src={image.src}
                      alt={image.alt}
                      title={image.alt}
                      width={752}
                      height={423}
                      className="w-full h-full object-cover"
                      priority={index === 0}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </Section>
  );
}

